import { useState, useEffect, useRef, useCallback } from 'react';
import GLOBALS from '../config/globals';
import { getEffectivePitcherStamina } from '../helpers/cardHelpers';
import { isUserPitching } from '../helpers/gameHelper';

/**
 * Tracks pitcher work across game events and detects when the user's
 * pitcher hits the stamina threshold.
 */
const usePitcherFatigue = ({ game, currentUser, currentPitcherCard, managerMode, setAutoPlay }) => {
    const [isPitcherFatiguedModalOpen, setIsPitcherFatiguedModalOpen] = useState(false);
    const pitcherWorkMapRef = useRef(new Map());

    // ── Accumulate pitcher work across game updates ──
    useEffect(() => {
        if (!game?.GameEvents) return;
        for (const ev of game.GameEvents) {
            if (ev.pitcherLineupID != null && ev.pitcherWork != null) {
                const key = String(ev.pitcherLineupID);
                const cur = pitcherWorkMapRef.current.get(key) ?? 0;
                if (ev.pitcherWork > cur) pitcherWorkMapRef.current.set(key, ev.pitcherWork);
            }
        }
    }, [game]);

    // ── Effective stamina for the current pitcher ──
    const currentPitcherEffectiveStamina = currentPitcherCard
        ? getEffectivePitcherStamina(currentPitcherCard, currentPitcherCard.CardInventory?.upgrades)
        : GLOBALS.DEFAULT_PITCHER_STAMINA;

    /**
     * Called after each at-bat to check if the pitcher just hit the threshold.
     * Opens the fatigue modal (or pauses auto if manager mode is off).
     */
    const checkPitcherFatigue = useCallback((updatedGame) => {
        const nextWork = updatedGame.nextPitcherWork ?? 0;
        const nextPitcher = updatedGame.GameEvents?.[0]?.nextPitcherLineup?.Card;
        const pitcherStaminaData = updatedGame.nextPitcherStamina;
        const baseStamina = pitcherStaminaData?.stamina ?? nextPitcher?.stamina;
        if (baseStamina == null) return;

        const effStamina = getEffectivePitcherStamina(
            { stamina: baseStamina },
            pitcherStaminaData?.upgrades
        );
        const pitching = isUserPitching(updatedGame, currentUser);
        if (nextWork >= effStamina && pitching) {
            // Auto-manager handles subs directly — only show modal for manual mode
            if (!managerMode && nextWork === effStamina) {
                setAutoPlay(false);
                setIsPitcherFatiguedModalOpen(true);
            }
        }
    }, [currentUser, managerMode, setAutoPlay]);

    return {
        isPitcherFatiguedModalOpen,
        setIsPitcherFatiguedModalOpen,
        pitcherWorkMapRef,
        currentPitcherEffectiveStamina,
        checkPitcherFatigue,
    };
};

export default usePitcherFatigue;
